import React, { Component } from 'react';
import { connect } from 'react-redux';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { ColumnGroup } from 'primereact/columngroup';
import { Row } from 'primereact/row';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { Toast } from 'primereact/toast';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { DROPDOWN_CONFIG, DATATABLE_CONFIG, USER_SUBMENU_ID } from '../../../shared/Config';
import { HASACCESS } from '../../../shared/utility';

import RestaurantDailySalesReportHeader from './RestaurantDailySalesReportHeader';
import './RestaurantDailySalesReport.scss';

// Dummy restaurants — replace with API/Redux data
const RESTAURANTS = [
    { label: 'Chenthoor Restaurant', value: 'Chenthoor Restaurant' },
    { label: 'Chenthoor Restaurant - 2', value: 'Chenthoor Restaurant - 2' },
];

const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;

const pad = (n) => String(n).padStart(2, '0');

// dd.mm.yy, same as the reference sheet
const formatDate = (d) => `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${String(d.getFullYear()).slice(-2)}`;

const formatNum = (v) => (v === null || v === undefined || v === '' ? '' : Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

function generateDateRange(from, to) {
    const dates = [];
    const cur = new Date(from.getFullYear(), from.getMonth(), from.getDate());
    const end = new Date(to.getFullYear(), to.getMonth(), to.getDate());
    while (cur <= end) {
        dates.push(new Date(cur));
        cur.setDate(cur.getDate() + 1);
    }
    return dates;
}

// Dummy daily bills — replace with API data: { date, billFrom, billTo, amount, discount, cancel, gst }
function seedDay(date, index, prevBillTo) {
    const seed = date.getDate() * 37 + (date.getMonth() + 1) * 11 + index * 7;
    const billCount = 15 + (seed % 45);
    const amount = 3000 + ((seed * 913) % 30000);
    return {
        billFrom: prevBillTo + 1,
        billTo: prevBillTo + billCount,
        amount,
        discount: 0,
        cancel: 0,
        gst: round2(amount * 0.05),
    };
}

// Derived columns, matching the sheet: Actual Sales = A - B - Cancel - C,
// Grand Sales = running total of Actual Sales, Average = Grand Sales / days so far.
function buildRows(dates) {
    let billTo = 0;
    let grand = 0;
    return dates.map((date, i) => {
        const d = seedDay(date, i, billTo);
        billTo = d.billTo;
        const actual = round2(d.amount - d.discount - d.cancel - d.gst);
        grand = round2(grand + actual);
        return {
            id: i,
            date,
            ...d,
            actual,
            grand,
            average: round2(grand / (i + 1)),
        };
    });
}

const COLUMN_LABELS = ['DATE', 'BILL FROM', 'BILL TO', 'AMOUNT (A)', 'DISCOUNT (B)', 'CANCEL', 'GST (C)', 'ACTUAL SALES (A-B-C)', 'GRAND SALES', 'AVERAGE SALES'];



class RestaurantDailySalesReport extends Component {
    state = {
        Restaurant: RESTAURANTS[0].value,
        fromDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
        toDate: new Date(),
        tableData: [],
        searched: false,
        btnLoading: null, // key of the action currently running: search | showAll | clear | pdf | excel
    };

    componentDidMount() {
        this.getAccess = HASACCESS(USER_SUBMENU_ID);
    }

    // Same loader as the rest of the project (full-page .page-loader overlay) — shown until the action
    // completes; further clicks are ignored meanwhile.
    runWithLoader = (key, action) => {
        if (this.state.btnLoading) return;
        this.setState({ btnLoading: key }, () => {
            setTimeout(() => {
                try {
                    action();
                } finally {
                    if (!this.unmounted) this.setState({ btnLoading: null });
                }
            }, 300);
        });
    };

    handleSearch = () => this.runWithLoader('search', this.handleSearchAction);
    handleShowAll = () => this.runWithLoader('showAll', this.handleShowAllAction);
    handleClear = () => this.runWithLoader('clear', this.handleClearAction);
    handleExportExcel = () => this.runWithLoader('excel', this.handleExportExcelAction);
    handleExportPdf = () => this.runWithLoader('pdf', this.handleExportPdfAction);

    componentWillUnmount() {
        this.unmounted = true;
    }

    handleSearchAction = () => {
        const { fromDate, toDate, Restaurant } = this.state;
        if (!Restaurant) {
            this.growl.show({ severity: 'error', summary: 'Error', detail: 'Please select Restaurant' });
            return;
        }
        if (!fromDate || !toDate) {
            this.growl.show({ severity: 'error', summary: 'Error', detail: 'Please select From Date and To Date' });
            return;
        }
        if (fromDate > toDate) {
            this.growl.show({ severity: 'error', summary: 'Error', detail: 'From Date cannot be after To Date' });
            return;
        }
        this.setState({ tableData: buildRows(generateDateRange(fromDate, toDate)), searched: true });
    };

    handleShowAllAction = () => {
        const now = new Date();
        const from = new Date(now.getFullYear(), now.getMonth(), 1);
        this.setState({ fromDate: from, toDate: now, tableData: buildRows(generateDateRange(from, now)), searched: true });
    };

    handleClearAction = () => {
        const now = new Date();
        this.setState({ Restaurant: RESTAURANTS[0].value, fromDate: new Date(now.getFullYear(), now.getMonth(), 1), toDate: now, tableData: [], searched: false });
    };

    getTotals = () => {
        const { tableData } = this.state;
        const sum = (k) => round2(tableData.reduce((a, r) => a + Number(r[k] || 0), 0));
        const last = tableData[tableData.length - 1];
        return {
            amount: sum('amount'),
            discount: sum('discount'),
            cancel: sum('cancel'),
            gst: sum('gst'),
            actual: sum('actual'),
            grand: last ? last.grand : 0,
            average: last ? last.average : 0,
        };
    };

    buildExportMatrix = () => {
        const { tableData } = this.state;
        const t = this.getTotals();
        const body = tableData.map(r => [formatDate(r.date), r.billFrom, r.billTo, r.amount, r.discount || '', r.cancel || '', r.gst, r.actual, r.grand, r.average]);
        const total = ['TOTAL', '', '', t.amount, t.discount || '', t.cancel || '', t.gst, t.actual, t.grand, t.average];
        return { body, total };
    };

    reportTitle = () => {
        const { Restaurant, fromDate, toDate } = this.state;
        return `${String(Restaurant).toUpperCase()} - DAILY SALES REPORT (${formatDate(fromDate)} to ${formatDate(toDate)})`;
    };

    handleExportExcelAction = () => {
        const { body, total } = this.buildExportMatrix();
        if (body.length === 0) {
            this.growl.show({ severity: 'error', summary: 'Error', detail: 'No data to export' });
            return;
        }
        const ws = XLSX.utils.aoa_to_sheet([[this.reportTitle()], COLUMN_LABELS, ...body, total]);
        ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: COLUMN_LABELS.length - 1 } }];
        ws['!cols'] = COLUMN_LABELS.map(() => ({ wch: 16 }));
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Daily Sales Report');
        const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        saveAs(new Blob([buf], { type: 'application/octet-stream' }), 'Restaurant_Daily_Sales_Report.xlsx');
    };

    handleExportPdfAction = () => {
        const { body, total } = this.buildExportMatrix();
        if (body.length === 0) {
            this.growl.show({ severity: 'error', summary: 'Error', detail: 'No data to export' });
            return;
        }
        const fmt = (row) => row.map((v, i) => (i >= 3 && v !== '' ? formatNum(v) : v));
        const doc = new jsPDF('l', 'mm', 'a4');
        doc.setFontSize(12);
        doc.text(this.reportTitle(), 14, 10);
        doc.autoTable({
            head: [COLUMN_LABELS],
            body: [...body.map(fmt), fmt(total)],
            startY: 15,
            styles: { fontSize: 8, cellPadding: 1.5, halign: 'right' },
            columnStyles: { 0: { halign: 'center' }, 1: { halign: 'center' }, 2: { halign: 'center' } },
            headStyles: { fillColor: [244, 176, 176], textColor: 0, halign: 'center' },
            // Vertical separator between every column (left edge of each cell + right edge of the last one)
            didDrawCell: (d) => {
                const { x, y, width, height } = d.cell;
                doc.setDrawColor(150);
                doc.setLineWidth(0.2);
                doc.line(x, y, x, y + height);
                if (d.column.index === COLUMN_LABELS.length - 1) doc.line(x + width, y, x + width, y + height);
            },
            didParseCell: (d) => {
                if (d.section === 'body' && d.row.index === body.length) d.cell.styles.fontStyle = 'bold';
            },
        });
        doc.save('Restaurant_Daily_Sales_Report.pdf');
    };

    numTemplate = (field) => (row) => formatNum(row[field]);

    // Discount / Cancel stay blank when zero, as in the reference sheet
    optionalNumTemplate = (field) => (row) => (row[field] ? formatNum(row[field]) : '');

    render() {
        if (this.getAccess !== undefined && this.getAccess.viewAccess === false) {
            this.props.history.push('/dashboard');
        }

        const { tableData, fromDate, toDate, searched, Restaurant, btnLoading } = this.state;
        const busy = btnLoading !== null;
        const t = searched ? this.getTotals() : {};
        const right = { textAlign: 'right' };
        const center = { textAlign: 'center' };

        const headerGroup = (
            <ColumnGroup>
                <Row>
                    <Column header={this.reportTitle()} colSpan={10} style={{ textAlign: 'center', background: '#f4b0b0', color: '#000' }} />
                </Row>
                <Row>
                    <Column header="DATE" rowSpan={2} style={{ minWidth: '110px', ...center }} />
                    <Column header="BILL NO" colSpan={2} style={center} />
                    <Column header="AMOUNT (A)" rowSpan={2} style={{ minWidth: '110px', ...center }} />
                    <Column header="DISCOUNT (B)" rowSpan={2} style={{ minWidth: '110px', ...center }} />
                    <Column header="CANCEL" rowSpan={2} style={{ minWidth: '100px', ...center }} />
                    <Column header="GST (C)" rowSpan={2} style={{ minWidth: '110px', ...center }} />
                    <Column header="ACTUAL SALES (A-B-C)" rowSpan={2} style={{ minWidth: '150px', ...center }} />
                    <Column header="GRAND SALES" rowSpan={2} style={{ minWidth: '130px', ...center }} />
                    <Column header="AVERAGE SALES" rowSpan={2} style={{ minWidth: '130px', ...center }} />
                </Row>
                <Row>
                    <Column header="FROM" style={{ minWidth: '80px', ...center }} />
                    <Column header="TO" style={{ minWidth: '80px', ...center }} />
                </Row>
            </ColumnGroup>
        );

        const footerGroup = (
            <ColumnGroup>
                <Row>
                    <Column footer="TOTAL" colSpan={3} footerStyle={center} />
                    <Column footer={formatNum(t.amount)} footerStyle={right} />
                    <Column footer={t.discount ? formatNum(t.discount) : ''} footerStyle={right} />
                    <Column footer={t.cancel ? formatNum(t.cancel) : ''} footerStyle={right} />
                    <Column footer={formatNum(t.gst)} footerStyle={right} />
                    <Column footer={formatNum(t.actual)} footerStyle={right} />
                    <Column footer={formatNum(t.grand)} footerStyle={right} />
                    <Column footer={formatNum(t.average)} footerStyle={right} />
                </Row>
            </ColumnGroup>
        );

        return (
            <div className="p-grid padding">
                {busy && <div className="page-loader"></div>}
                <Toast ref={(el) => this.growl = el} />

                <div className="p-col-12">
                    <div className="card card-w-title" style={{ position: 'relative' }}>
                        <RestaurantDailySalesReportHeader />

                        <div className="p-grid p-align-end">
                            <div className="p-col-12 p-md-3 p-lg-3 p-d-flex p-flex-column p-gap-2">
                                <label className="label">Restaurant</label>
                                <Dropdown
                                    placeholder="Restaurant"
                                    name="Restaurant"
                                    options={RESTAURANTS}
                                    value={Restaurant}
                                    onChange={(e) => this.setState({ Restaurant: e.value })}
                                    filter
                                    autoWidth
                                    className="p-col-12"
                                    {...DROPDOWN_CONFIG}
                                />
                            </div>

                            <div className="p-col-12 p-md-2 p-lg-2 p-d-flex p-flex-column p-gap-2">
                                <label className="label">From Date</label>
                                <Calendar placeholder="From Date" value={fromDate} dateFormat="dd/mm/yy" onChange={(e) => this.setState({ fromDate: e.value })} />
                            </div>

                            <div className="p-col-12 p-md-2 p-lg-2 p-d-flex p-flex-column p-gap-2">
                                <label className="label">To Date</label>
                                <Calendar placeholder="To Date" value={toDate} dateFormat="dd/mm/yy" onChange={(e) => this.setState({ toDate: e.value })} />
                            </div>

                            <div className="p-col-12 p-md-5 p-lg-5 p-d-flex p-ai-end p-gap-2">
                                <Button label="Search" icon="pi pi-search" className="p-button-primary" onClick={this.handleSearch} disabled={busy} />
                                <Button label="Show All" icon="pi pi-list" className="p-button-secondary p-ml-2" onClick={this.handleShowAll} disabled={busy} />
                                <Button label="Clear" icon="pi pi-times" className="p-button-secondary p-ml-2" onClick={this.handleClear} disabled={busy} />
                            </div>

                            <div className="p-col-12 p-d-flex p-jc-end p-gap-2 p-mt-1">
                                <Button label="Export as Excel" icon="pi pi-file-excel" className="p-button-warning" onClick={this.handleExportExcel} disabled={!searched || busy} />
                                <Button label="Export as PDF" icon="pi pi-file-pdf" className="p-button-danger p-ml-2" onClick={this.handleExportPdf} disabled={!searched || busy} />
                            </div>
                        </div>

                        {searched && (
                            <div className="p-col-12" style={{ marginTop: '1em', overflowX: 'auto' }}>
                                <DataTable
                                    value={tableData}
                                    dataKey="id"
                                    headerColumnGroup={headerGroup}
                                    footerColumnGroup={footerGroup}
                                    scrollable
                                    scrollHeight="500px"
                                    style={{ width: '100%' }}
                                    emptyMessage="No records found"
                                    {...DATATABLE_CONFIG}
                                >
                                    <Column field="date" body={(r) => formatDate(r.date)} style={{ minWidth: '110px', ...center }} />
                                    <Column field="billFrom" style={{ minWidth: '80px', ...center }} />
                                    <Column field="billTo" style={{ minWidth: '80px', ...center }} />
                                    <Column field="amount" body={this.numTemplate('amount')} style={{ minWidth: '110px', ...right }} />
                                    <Column field="discount" body={this.optionalNumTemplate('discount')} style={{ minWidth: '110px', ...right }} />
                                    <Column field="cancel" body={this.optionalNumTemplate('cancel')} style={{ minWidth: '100px', ...right }} />
                                    <Column field="gst" body={this.numTemplate('gst')} style={{ minWidth: '110px', ...right }} />
                                    <Column field="actual" body={this.numTemplate('actual')} style={{ minWidth: '150px', ...right }} />
                                    <Column field="grand" body={this.numTemplate('grand')} style={{ minWidth: '130px', ...right }} />
                                    <Column field="average" body={this.numTemplate('average')} style={{ minWidth: '130px', ...right }} />
                                </DataTable>
                            </div>
                        )}

                        {!searched && (
                            <div className="p-d-flex p-jc-center p-ai-center" style={{ minHeight: '200px', color: '#aaa', fontSize: '15px' }}>
                                <i className="pi pi-search p-mr-2" />
                                Select a date range and click Search to view the report.
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }
}

const mapStateToProps = state => ({
    loggedUserDetail: state.auth.userObject,
});

export default connect(mapStateToProps)(RestaurantDailySalesReport);
